import { useState, useEffect } from "react";

// Custom event name for syncing between instances
const STORAGE_SYNC_EVENT = "localStorageSync";

export function useLocalStorage<T>(key: string, initialValue: T) {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState<T>(() => {
        if (typeof window === "undefined") {
            return initialValue;
        }
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error);
            return initialValue;
        }
    });

    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = (value: T | ((val: T) => T)) => {
        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            setStoredValue(valueToStore);
            if (typeof window !== "undefined") {
                window.localStorage.setItem(key, JSON.stringify(valueToStore));
                // Dispatch custom event to sync other instances
                window.dispatchEvent(
                    new CustomEvent(STORAGE_SYNC_EVENT, {
                        detail: { key, value: valueToStore },
                    }),
                );
            }
        } catch (error) {
            console.warn(`Error setting localStorage key "${key}":`, error);
        }
    };

    const clearValue = () => {
        try {
            if (typeof window !== "undefined") {
                window.localStorage.removeItem(key);
                // Dispatch custom event to sync other instances
                window.dispatchEvent(
                    new CustomEvent(STORAGE_SYNC_EVENT, {
                        detail: { key, value: null },
                    }),
                );
            }
            setStoredValue(initialValue);
        } catch (error) {
            console.warn(`Error clearing localStorage key "${key}":`, error);
        }
    };

    // Listen for changes to this key in other tabs/windows and instances
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent | CustomEvent) => {
            const eventKey = e instanceof StorageEvent ? e.key : e.detail.key;
            if (eventKey === key) {
                try {
                    if (e instanceof StorageEvent) {
                        if (e.newValue === null) {
                            setStoredValue(initialValue);
                        } else if (e.newValue) {
                            setStoredValue(JSON.parse(e.newValue));
                        }
                    } else {
                        // Handle custom event
                        if (e.detail.value === null) {
                            setStoredValue(initialValue);
                        } else {
                            setStoredValue(e.detail.value);
                        }
                    }
                } catch (error) {
                    console.warn(`Error parsing localStorage value for key "${key}":`, error);
                }
            }
        };

        window.addEventListener("storage", handleStorageChange);
        window.addEventListener(STORAGE_SYNC_EVENT, handleStorageChange as EventListener);
        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener(STORAGE_SYNC_EVENT, handleStorageChange as EventListener);
        };
    }, [key, initialValue]);

    return [storedValue, setValue, clearValue] as const;
}
